import dayjs from "dayjs";
import { CalendarDays, MapPin } from "lucide-react";
import { useMemo, type FC } from "react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import { AssignedUsersAvatarStack } from "@/features/foreignNationals/components/AssignedUsersAvatarStack";
import { PanelEmpty } from "@/features/clientProfile/components/PanelState";
import {
  AGENDA_DAYS,
  agendaDays,
  eventTone,
  formatEventTime,
  type CalendarEvent
} from "../calendarModel";
import { reminderFocusDomId } from "../remindersModel";
import { EventDot } from "./EventChip";

type Props = {
  anchor: Date;
  events: CalendarEvent[];
  highlightedId: string | null;
  onOpenEvent: (event: CalendarEvent) => void;
  onCreate: () => void;
  now?: Date;
};

const StatusBadge: FC<{ event: CalendarEvent; now?: Date }> = ({
  event,
  now
}) => {
  const tone = eventTone(event, now);
  if (event.status === "COMPLETED")
    return <Badge variant="secondary">Completed</Badge>;
  if (event.status === "DISMISSED")
    return <Badge variant="outline">Dismissed</Badge>;
  if (tone === "overdue") return <Badge variant="destructive">Overdue</Badge>;
  return null;
};

/** Chronological list of the next 30 days, grouped by day. */
export const AgendaView: FC<Props> = ({
  anchor,
  events,
  highlightedId,
  onOpenEvent,
  onCreate,
  now = new Date()
}) => {
  const days = useMemo(() => agendaDays(events, anchor), [anchor, events]);

  if (days.length === 0) {
    return (
      <PanelEmpty
        icon={CalendarDays}
        title="Nothing scheduled"
        description={`No appointments or reminders in the next ${AGENDA_DAYS} days.`}
        action={{ label: "New event", onClick: onCreate }}
      />
    );
  }

  return (
    <div className="h-full min-h-0 overflow-y-auto">
      <div className="mx-auto flex max-w-3xl flex-col gap-6 p-4">
        {days.map((day) => {
          const isToday = dayjs(day.date).isSame(now, "day");
          return (
            <section
              key={day.key}
              aria-label={dayjs(day.date).format("dddd, MMMM D")}
            >
              <header className="mb-2 flex items-baseline gap-2">
                <span
                  className={cn(
                    "text-sm font-semibold",
                    isToday ? "text-primary" : "text-foreground"
                  )}
                >
                  {dayjs(day.date).format("ddd, MMM D")}
                </span>
                {isToday ? (
                  <span className="text-xs text-muted-foreground">Today</span>
                ) : null}
              </header>
              <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
                {day.events.map((event) => {
                  const tone = eventTone(event, now);
                  const domId = reminderFocusDomId(event.id);
                  return (
                    <li key={event.id}>
                      <button
                        type="button"
                        id={domId}
                        data-focus-id={domId}
                        onClick={() => onOpenEvent(event)}
                        className={cn(
                          "flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors hover:bg-fill-1",
                          highlightedId === domId &&
                            "bg-primary/10 ring-1 ring-primary/40 ring-inset"
                        )}
                      >
                        <span className="w-28 shrink-0 text-xs text-muted-foreground tabular-nums">
                          {formatEventTime(event)}
                        </span>
                        <EventDot event={event} />
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span
                            className={cn(
                              "truncate font-medium",
                              tone === "done" &&
                                "text-muted-foreground line-through"
                            )}
                          >
                            {event.title}
                          </span>
                          {event.location ? (
                            <span className="flex items-center gap-1 truncate text-xs text-muted-foreground">
                              <MapPin className="size-3" /> {event.location}
                            </span>
                          ) : null}
                        </span>
                        <StatusBadge event={event} now={now} />
                        {event.assignees.length > 0 ? (
                          <AssignedUsersAvatarStack
                            userIds={event.assignees}
                            maxVisible={3}
                            nonInteractive
                          />
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
};
